import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BackdropGesture } from './backdrop-dismiss.js';
import { BUBBLE_WIDTH, placeBubble } from './explain-place.js';
import type { BubblePlace } from './explain-place.js';
import { NO_GESTURE, dismisses, press, release } from './backdrop-dismiss.js';
import './ui.css';

/**
 * The shared primitives. Every screen is built from these so the anatomy stays
 * identical everywhere and hands learn it once (SCOPE §2.2-4).
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  readonly variant?: ButtonVariant;
  readonly size?: 'medium' | 'large';
  /**
   * Why this action is unavailable.
   *
   * Supplying a reason disables the button *and shows the reason*. SCOPE §2.2-7
   * is explicit: "illegal actions are disabled with a visible reason, not
   * error-dialogued after the fact." A greyed-out button with no explanation is
   * the thing that makes staff hunt for a manager.
   */
  readonly disabledReason?: string | undefined;
  readonly busy?: boolean;
  readonly children: ReactNode;
}

/**
 * An indeterminate progress indicator.
 *
 * SCOPE §2.2-6 requires every action to acknowledge within 100ms. A button that
 * merely greys out reads as broken — the operator cannot tell "working" from
 * "refused", and presses it again. Motion here is directing attention, not
 * decoration (§2.2-8), so it is small, quiet, and only present while waiting.
 */
export function Spinner({ label = 'Working' }: { label?: string }) {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="spinner__ring" aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export function Button({
  variant = 'secondary',
  size = 'medium',
  disabledReason,
  busy = false,
  children,
  className,
  ...rest
}: ButtonProps) {
  const reasonId = useId();
  const isDisabled = disabledReason !== undefined || busy;

  return (
    <span className="button-wrap">
      <button
        type="button"
        {...rest}
        className={`button button--${variant} button--${size}${className ? ` ${className}` : ''}`}
        disabled={isDisabled}
        aria-busy={busy || undefined}
        aria-describedby={disabledReason !== undefined ? reasonId : undefined}
      >
        {busy ? <Spinner /> : null}
        {children}
      </button>
      {disabledReason !== undefined ? (
        <span className="button-reason" id={reasonId}>
          {disabledReason}
        </span>
      ) : null}
    </span>
  );
}

export type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Card({
  title,
  actions,
  children,
  padded = true,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
  /** One more class on the card, for a screen that needs to change how it clips. */
  className?: string;
}) {
  return (
    <section className={className === undefined ? 'card' : `card ${className}`}>
      {title !== undefined ? (
        <header className="card__header">
          <h3 className="label-micro">{title}</h3>
          {actions}
        </header>
      ) : null}
      <div className={padded ? 'card__body' : undefined}>{children}</div>
    </section>
  );
}

/**
 * The one screen anatomy (SCOPE §2.2-4): context and search on top, work area
 * in the middle, money and actions pinned in fixed positions at the bottom.
 *
 * The action bar is where Fitts's law is paid: the primary action is the
 * largest control and always sits bottom-right, and destructive controls are
 * physically separated from it on the far left so a fast hand cannot hit Void
 * reaching for Pay.
 */
export function Screen({
  title,
  subtitle,
  context,
  children,
  destructiveAction,
  secondaryActions,
  primaryAction,
  width = 'default',
  fixedFrame = false,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  context?: ReactNode;
  children: ReactNode;
  destructiveAction?: ReactNode;
  secondaryActions?: ReactNode;
  primaryAction?: ReactNode;
  /**
   * `wide` for back-office tables.
   *
   * The default cap is tuned for the register: read at arm's length, in a
   * bright shop, with one focal point (§2.1). A screen full of columns is read
   * at a desk, and squeezing it to register width buys nothing — it just turns
   * a table into a horizontal scroll, which hides whatever is in the last
   * column. Not a licence to be dense: the same type scale and targets apply.
   */
  width?: 'default' | 'wide';
  /**
   * The anatomy §2.2-4 actually describes: **the frame never scrolls, the work
   * area does.**
   *
   * The default lets the whole page scroll and pins only the action bar, which
   * was fine while the header carried nothing but a title. Once controls that
   * *build* the document live up there (decision R3) it stops being fine — a
   * control that scrolls away after the eleventh item is not in a fixed
   * position, and fixed positions are the entire point of the anatomy. That is
   * the same failure decision 55 was fixing when it moved *Add customer* to the
   * bottom, and the cause was scroll rather than placement.
   *
   * Opt-in rather than universal for now: it changes how every screen scrolls,
   * and this batch has only proved it on the register.
   */
  fixedFrame?: boolean;
}) {
  const hasActions =
    destructiveAction !== undefined ||
    secondaryActions !== undefined ||
    primaryAction !== undefined;

  return (
    <div
      className={`screen${width === 'wide' ? ' screen--wide' : ''}${
        fixedFrame ? ' screen--fixed-frame' : ''
      }`}
    >
      <header className="screen__header">
        <div>
          <h1>{title}</h1>
          {subtitle !== undefined ? <p className="text-secondary">{subtitle}</p> : null}
        </div>
        {context}
      </header>

      <main className="screen__body">{children}</main>

      {hasActions ? (
        <footer className="screen__actions">
          <div className="screen__actions-destructive">{destructiveAction}</div>
          <div className="screen__actions-primary">
            {secondaryActions}
            {primaryAction}
          </div>
        </footer>
      ) : null}
    </div>
  );
}

/** A headline figure. Size carries the hierarchy; colour is left alone. */
export function Stat({
  label,
  value,
  tone = 'neutral',
  emphasis = false,
}: {
  label: ReactNode;
  value: ReactNode;
  tone?: BadgeTone;
  emphasis?: boolean;
}) {
  return (
    <div className={`stat${emphasis ? ' stat--emphasis' : ''}`}>
      <div className="label-micro">{label}</div>
      <div className={`stat__value num stat__value--${tone}`}>{value}</div>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}) {
  const id = useId();
  const hintId = useId();

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, 'aria-describedby': hint !== undefined ? hintId : undefined })}
      {hint !== undefined ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Progressive disclosure (SCOPE §2.2-2, Hick's law).
 *
 * Rare and advanced options live behind exactly one "More" affordance, so the
 * visible choices at any decision point stay under seven.
 */
export function More({ label, children }: { label: string; children: ReactNode }) {
  return (
    <details className="more">
      <summary className="more__summary">{label}</summary>
      <div className="more__body">{children}</div>
    </details>
  );
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: BadgeTone;
  title?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`callout callout--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {title !== undefined ? <strong className="callout__title">{title}</strong> : null}
      <div>{children}</div>
    </div>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="code">{children}</span>;
}

/**
 * Second-level navigation inside a section.
 *
 * The top bar is at the seven-choice ceiling §2.2-2 sets, so a section that
 * grows grows *downwards* rather than adding an eighth tab. Rendered as a row
 * of named targets rather than a dropdown: under seven entries, a name you can
 * see is one you can reach without discovering it first (§2.2-3), and a direct
 * target is cheaper to hit than a menu that has to be opened (§2.2-1).
 *
 * Entries the viewer cannot use are filtered out by the caller, never disabled
 * — a control that always refuses is worse than no control (§2.2-7).
 */
export function SectionNav<Key extends string>({
  label,
  current,
  items,
  onChange,
}: {
  label: string;
  current: Key;
  items: ReadonlyArray<{ readonly key: Key; readonly label: string }>;
  onChange: (key: Key) => void;
}) {
  // One entry is not a decision, and a picker showing a single option reads as
  // though something is missing.
  if (items.length < 2) return null;

  return (
    <div className="section-nav" role="tablist" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={current === item.key}
          className={`section-nav__item${current === item.key ? ' section-nav__item--on' : ''}`}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/** Page- or panel-level waiting. Same signal as the button, larger. */
export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading-panel">
      <Spinner label={label} />
      <span>{label}…</span>
    </div>
  );
}

/**
 * A figure that explains where it came from.
 *
 * SCOPE §2-9 says every EOD figure traces to individual tender records — but a
 * number nobody can account for is not trusted even when it is right, and an
 * untrusted total gets re-added by hand on a calculator. So each derived figure
 * carries its own derivation.
 *
 * Deliberately not hover-only: a counter is a touch environment, and a tooltip
 * that needs a mouse is invisible to half the people using it. This is a real
 * button — tap, click, or focus and press Enter.
 */
export function Explain({ label, children }: { label: string; children: ReactNode }) {
  const [position, setPosition] = useState<BubblePlace | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  /**
   * The bubble is rendered to `document.body` and positioned from the
   * trigger's viewport rect.
   *
   * Absolutely positioning it next to the trigger looked simpler and was
   * wrong: inside a modal or any scrolling container it gets clipped at the
   * edge, which is exactly where the interesting figures live. Portalling out
   * and clamping to the viewport means it is readable from anywhere.
   */
  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    // Above or below, decided once — it never covers the ? (decision 261).
    setPosition(placeBubble(rect, window.innerWidth));
  };

  const hide = () => setPosition(null);
  const open = position !== null;

  return (
    <span className="explain">
      <button
        ref={triggerRef}
        type="button"
        className="explain__trigger"
        aria-label={`How ${label} is worked out`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => (open ? hide() : show())}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        ?
      </button>
      {position !== null
        ? createPortal(
            <span
              className="explain__bubble"
              id={id}
              role="tooltip"
              style={{
                top: position.top,
                left: position.left,
                width: BUBBLE_WIDTH,
                transform: position.above ? 'translateY(-100%)' : undefined,
              }}
            >
              {children}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

/**
 * A real modal, on the native `<dialog>` element.
 *
 * `showModal()` supplies the focus trap, the Escape key, the inert background
 * and the backdrop — all things a hand-rolled div gets wrong. It also replaces
 * `window.prompt`, which cannot be styled, cannot be validated, and looks like
 * the browser is asking rather than the product.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  primaryAction,
  secondaryAction,
  destructiveAction,
  tone = 'neutral',
  wide = false,
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  /**
   * Pinned to the far left, away from the primary action (§2.2-1, Fitts).
   * `Screen` has had this since the register was built; a modal that asks a
   * destructive question had nowhere to put the control but next to Save.
   */
  destructiveAction?: ReactNode;
  tone?: 'neutral' | 'danger';
  /** Wider frame for tabular content. */
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  /**
   * Which end of the current pointer gesture landed on the backdrop.
   *
   * A ref rather than state: nothing renders from it, and re-rendering the
   * modal on every pointer press is exactly the sort of churn that steals focus
   * from an input somebody is typing into.
   */
  const gesture = useRef<BackdropGesture>(NO_GESTURE);
  /** The title names the dialog, so a screen reader says what opened (2026-09-24). */
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
    // A half-finished gesture from the modal that just closed must not make the
    // first click on this one a dismissal.
    gesture.current = NO_GESTURE;
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={`modal modal--${tone}${wide ? ' modal--wide' : ''}`}
      aria-labelledby={titleId}
      /*
       * Dismissal is bound to `cancel` (Escape) and a backdrop click — never to
       * the `close` event.
       *
       * `close` also fires when *we* call `dialog.close()` because `open` went
       * false, so routing it to `onClose` made one modal handing off to another
       * self-destruct: opening the hold dialog closed the cancel dialog, whose
       * close event then reset the state back to "no dialog". The handoff
       * silently became a dismissal.
       */
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      /*
       * A dismissal is a press *and* a release on the backdrop — never the
       * target of the click alone.
       *
       * The backdrop is a pseudo-element of the dialog, so a click on it
       * targets the dialog itself while a click on content targets a child.
       * That makes `event.target === dialog` look like it identifies a backdrop
       * click, and it is the single most frustrating bug the counter reported:
       * `click` fires on the **common ancestor** of where the pointer went down
       * and where it came up, so selecting text inside the modal and letting go
       * over the backdrop targets the dialog and threw the modal away with
       * whatever had been typed in it.
       *
       * Pointer events rather than mouse events so a touch screen and a stylus
       * behave identically — this is heading for a counter. The rule itself is
       * in `backdrop-dismiss.ts`, where it is unit-tested.
       */
      onPointerDown={(event) => {
        gesture.current = press(event.target === ref.current);
      }}
      onPointerUp={(event) => {
        gesture.current = release(gesture.current, event.target === ref.current);
      }}
      onClick={(event) => {
        const dismiss = dismisses(gesture.current, event.target === ref.current);
        gesture.current = NO_GESTURE;
        if (dismiss) onClose();
      }}
    >
      <div className="modal__head">
        <h2 className="modal__title" id={titleId}>
          {title}
        </h2>
        {description !== undefined ? <p className="text-secondary">{description}</p> : null}
      </div>

      {children !== undefined ? <div className="modal__body">{children}</div> : null}

      <div className="modal__actions">
        {destructiveAction === undefined ? null : (
          <div className="modal__actions-destructive">{destructiveAction}</div>
        )}
        {secondaryAction}
        {primaryAction}
      </div>
    </dialog>
  );
}
